import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CartAWB, PaymentCart } from '@cargos/sprintpay-models';
import { combineLatest, distinctUntilChanged, map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { CartBillService } from 'src/app/shared/services';
import { PromoCodesService } from 'src/app/shared/services/promo-code.service';
import { AwbSplitByFlightService } from '../../../cart-items/awb-split-by-flight/services/awb-split-by-flight.service';
import { FeesConfiguration } from '../../shared/models/convenience-fee.enum';

@Component({
    selector: 'app-payment-awb-by-flight',
    standalone: false,
    templateUrl: './payment-awb-by-flight.component.html',
    styleUrl: './payment-awb-by-flight.component.scss',
})
export class PaymentAwbByFlightComponent implements OnInit, OnChanges, OnDestroy {
    private unsubscribe$ = new Subject<void>();
    public flights: PaymentCart[][] = [];
    public panelOpenState = false;
    public nonReservationFee?: number | undefined = undefined;
    public isAwbPaidWithPromoCode?: boolean = false;
    public isOpenCloseDropDown = false;
    public awbList: CartAWB[] = [];
    public newFees: FeesConfiguration[] = [];
    public defaultFees: FeesConfiguration[] = [];
    @Input() awb: string;
    @Input() set first(value: boolean) {
        this.panelOpenState = value;
    }
    @Input() aboveThreshold: boolean;
    @Input() paymentRequest: PaymentCart;
    @Input() feeList: string[] = [];
    constructor(
        private cartBillService: CartBillService,
        private awbByFlightService: AwbSplitByFlightService,
        private cdr: ChangeDetectorRef,
        private promoCodesService: PromoCodesService
    ) {}

    ngOnInit(): void {
        this.subscribePaymentsByAWB();
        this.subscribeNonReservationFee();
        this.subscribeIsAWBPaidWithPromoCode();
        this.defaultFees = this.awbByFlightService.getConvenienceFeesByAWB();
        this.updateFeesInformation();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isAwbPaidWithPromoCode'] && changes['isAwbPaidWithPromoCode'].currentValue) {
            this.updateFeesInformation();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    subscribePaymentsByAWB(): void {
        combineLatest([
            this.cartBillService.getCartFilterByAWBAndFlights$(this.awb, this.aboveThreshold),
            this.cartBillService.getAWBInfoByAWB(this.awb, this.aboveThreshold),
        ])
            .pipe(
                map(([paymentsByAWB, awbList]) => {
                    const sortedFlights = paymentsByAWB.map((flight) =>
                        flight.sort((a, b) => {
                            return (a.isAccessorial ? 1 : 0) - (b.isAccessorial ? 1 : 0);
                        })
                    );
                    return { sortedFlights, awbList };
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                next: ({ sortedFlights, awbList }) => {
                    this.flights = sortedFlights;
                    this.awbList = awbList;
                    this.updateFeesInformation();
                },
            });
    }

    onOpenCloseDropDown(): void {
        this.isOpenCloseDropDown = !this.isOpenCloseDropDown;
    }

    subscribeNonReservationFee(): void {
        this.promoCodesService
            .getPromoCodeValueByAWB$(this.awb, this.aboveThreshold)
            .pipe(
                switchMap((nonReservationFee) => {
                    if (!!nonReservationFee) {
                        return this.subscribeIsAWBPaidWithPromoCode().pipe(
                            map((isAwbPaidWithPromoCode) => ({
                                isAwbPaidWithPromoCode,
                                nonReservationFee,
                            }))
                        );
                    }
                    return of(null);
                }),
                distinctUntilChanged(),
                takeUntil(this.unsubscribe$)
            )
            .subscribe({
                next: (promoCodeInfo) => {
                    this.nonReservationFee = promoCodeInfo?.nonReservationFee;
                    this.isAwbPaidWithPromoCode = promoCodeInfo?.isAwbPaidWithPromoCode;
                    this.defaultFees = this.defaultFees.filter((fee) => fee.feeName !== 'discountNonReservationFee');

                    if (promoCodeInfo?.isAwbPaidWithPromoCode) {
                        this.defaultFees.push({
                            feeName: 'discountNonReservationFee',
                            feeLabel: 'Discount Non Reservation Fee',
                            feeValue: -(this.nonReservationFee || 0),
                        });
                    }
                    this.updateFeesInformation();
                },
            });
    }
    subscribeIsAWBPaidWithPromoCode(): Observable<boolean> {
        return this.promoCodesService.isAwbPaidWithPromoCode$(this.awb);
    }

    onPanelOpen(): void {
        this.panelOpenState = true;
    }

    onPanelClose(): void {
        this.panelOpenState = false;
    }

    updateFeesInformation(): void {
        this.newFees = this.defaultFees
            .map((fee) => {
                let finalFeeValue = fee.feeValue;
                if (!finalFeeValue || (finalFeeValue === 0 && finalFeeValue >= 0)) {
                    const foundValue = this.awbList.find((awb) => {
                        const awbFeeValue = awb[fee.feeName];
                        return awbFeeValue && awbFeeValue > 0;
                    })?.[fee.feeName];
                    if (foundValue && foundValue > 0) {
                        finalFeeValue = foundValue;
                    }
                }

                return {
                    ...fee,
                    feeValue: finalFeeValue,
                };
            })
            .filter((fee) => fee.feeValue && (fee.feeValue > 0 || fee.feeValue < 0));

        this.cdr.detectChanges();
    }
}
